import React from 'react';
import axiosInstance from '../actions/AxiosInstance';
import api from '../actions/EndPoints';
import { log, setLastRequestTime, getFinancialAccounts } from '../Utils';

/**
 * Hook providing client-related API services
 * @param {Function} dispatch - Redux dispatch function for state updates
 * @returns {Object} Object containing client service functions
 */
export const useClientServices = (dispatch) => {
    /**
     * Fetches and processes client financial accounts data
     * @param {string} key - API key for authentication
     */
    const getFinAccounts = async (key) => {
        try {
            const getAccounts = await axiosInstance.get(api.financialAccountsAPI, {
                headers: { apikey: `${key}` }
            });
            if (getAccounts) {
                const internalAccounts = getFinancialAccounts(getAccounts?.data?.internalAccounts, 'internalAccounts');
                const externalAccounts = getFinancialAccounts(getAccounts?.data?.externalAccounts, 'externalAccounts');
                const manualAccounts = getAccounts?.data?.manualAccounts;
                dispatch({
                    type: 'FINANCIAL_ACCOUNTS',
                    financialAccounts: { internalAccounts, externalAccounts, manualAccounts }
                });
            }
        } catch (error) {
            setLastRequestTime();
            console.log(error);
            dispatch({ type: 'AUTH_FAILURE', isLoading: false, error });
            throw error;
        }
    };

    /**
     * Fetches active actions for the client
     * @param {string} key - API key for authentication
     */
    const callActionService = async (key) => {
        try {
            const getAction = await axiosInstance.get(api.cwvActionGetAPI, {
                headers: { apiKey: `${key}` }
            });
            dispatch({
                type: 'ACTION_SERVICE',
                activeActions: getAction.data
            });
        } catch (error) {
            log(console.log(error));
        }
    };

    /**
     * Fetches client user status and checks for missing critical information
     * @param {string} key - API key for authentication
     */
    const getClientUserStatus = async (key) => {
        try {
            const getData = await axiosInstance.get(api.clientUserStatus, {
                headers: { apikey: `${key}` }
            });
            if (getData) {
                dispatch({
                    type: 'CLIENT_USER_STATUS_RESPONSE',
                    userStatus: getData.data,
                });
                return getData;
            }
            return getData;
        } catch (error) {
            console.log('Get Client user Status Error: ', error);
            dispatch({ type: 'CLIENT_USER_STATUS_FAILURE', userStatusFailure: true });
            throw error;
        }
    };

    /**
     * Updates client user status with provided user information
     * @param {string} key - API key for authentication
     * @param {Object} payload - User data payload from the form or directly from the get call
     * @param {string} payload.firstName - User's first name
     * @param {string} payload.lastName - User's last name
     * @param {string} payload.emailAddress - User's email address
     * @returns {Promise<Object>} Promise resolving to the API response
     */
    const putClientUserStatus = async (key, payload) => {
        try {
            const headerPayload = {
                firstName: payload.firstName,
                lastName: payload.lastName,
                emailAddress: payload.emailAddress,
            }
            return await axiosInstance.put(api.clientUserStatus, headerPayload, {
                headers: { apikey: `${key}` }
            });
        }
        catch (error) {
            console.log('Put Client user Status Error: ', error);
            throw error;
        }
    };

    /**
     * Fetches Know Your Client (KYC) information
     * @param {string} key - API key for authentication
     */
    const getKycInfo = React.useCallback(async (key) => {
        try {
            const getData = await axiosInstance.get(api.knowYourClientAPI, {
                headers: { apikey: `${key}` }
            });
            if (getData) {
                dispatch({
                    type: 'KYC_RESPONSE',
                    kycData: getData.data,
                });
            }
        } catch (error) {
            dispatch({
                type: 'KYC_RESPONSE',
                isLoading: false
            });
            console.log(error);
            throw error;
        }
    }, [dispatch]);

    /**
     * Fetches client preferences data
     * @param {string} key - API key for authentication
     */
    const getClientPreferences = React.useCallback(async (key) => {
        try {
            const getData = await axiosInstance.get(api.clientPreferencesAPI, {
                headers: { apikey: `${key}` }
            });
            if (getData) {
                dispatch({
                    type: 'CLIENT_PREFERENCES_RESPONSE',
                    clientPreferences: getData.data,
                });
            }
        } catch (error) {
            dispatch({
                type: 'CLIENT_PREFERENCES_RESPONSE',
                isLoading: false
            });
            console.log(error);
            throw error;
        }
    }, [dispatch]);

    /**
     * Fetches Yodlee account aggregation response data
     * @param {string} key - API key for authentication
     */
    const getYodleeResponse = React.useCallback(async (key) => {
        try {
            const getResponse = await axiosInstance.get(api.yodleeError, {
                headers: { apikey: `${key}` }
            });
            if (getResponse) {
                const { data } = getResponse;
                dispatch({
                    type: 'YODLEE_RESPONSE',
                    lastRefreshDate: data[0]?.lastRefreshDate || null,
                    institutionName: data[0]?.institutionName || null,
                    errorStatus: data[0]?.errorStatus || null
                });
            }
        } catch (err) {
            log(console.log(err));
        }
    }, [dispatch]);

    return {
        getFinAccounts,
        callActionService,
        getClientUserStatus,
        putClientUserStatus,
        getKycInfo,
        getClientPreferences,
        getYodleeResponse
    };
};
